import React from 'react';
import { AttributeInputCallback } from '@forgerock/javascript-sdk';
import { AutocompleteInput, ChoiceSelect, LabeledInput, SectionTitle, SlideToggle } from 'Components';
import {
  AddressSearchApi,
  AddressSearchInput,
  AutocompletionService,
  extractPolicy,
  SearchError,
  mapFailedPoliciesToMessage,
  mapFailedPolicyToMessage,
  professionalAccountService,
  Search,
} from 'Services';

export type CHOICE_FIELD = 'custom_gender' | 'custom_professional';
export const CHOICE_LABELS = {
  custom_gender: ['Monsieur', 'Madame'],
  custom_gender_pro: ['Société', 'Établissement'],
  custom_professional: ['Particulier', 'Professionnel'],
};

export const DEFAULT_CHOICES = {
  custom_gender: 'Monsieur',
  custom_professional: 'Particulier',
};

const buildDefaultValue = (callback: AttributeInputCallback<string | number | boolean>) => {
  const inputValue = callback.getInputValue() as string;
  return (
    (callback.getInputValue() !== '' && CHOICE_LABELS.custom_gender.includes(inputValue)
      ? inputValue
      : DEFAULT_CHOICES[callback.getName() as CHOICE_FIELD]) || ''
  );
};

const handleChoiceSelect = (callback: AttributeInputCallback<string | number | boolean>) => {
  const labels = CHOICE_LABELS[callback.getName() as CHOICE_FIELD] || [];
  const defaultValue = buildDefaultValue(callback);
  defaultValue !== '' && callback.setValue(defaultValue);

  return (
    <ChoiceSelect
      key={callback.payload._id}
      labels={labels}
      name={callback.getName()}
      onChange={(event) => {
        if (callback.getName() === 'custom_professional') {
          event.target.value === 'Professionnel'
            ? professionalAccountService.setFieldsToProfessional()
            : professionalAccountService.setFieldsToPersonnal();
        }
        callback.setValue(event.currentTarget.value);
      }}
      required={callback.isRequired()}
      defaultValue={defaultValue}
    />
  );
};

const addressGroupedElements = ['custom_darty_building', 'custom_darty_stairs', 'custom_darty_floor', 'custom_darty_door'];

const mapCallbackToType = (callback: AttributeInputCallback<string | number | boolean>): string => {
  switch (callback.getName()) {
    case 'mail':
      return 'email';
    case 'custom_birthday':
      return 'date';
    case 'telephoneNumber':
    case 'postalCode':
      return 'tel';
    default:
      return 'text';
  }
};

const formatValue = (event: React.ChangeEvent<HTMLInputElement>, name: string) => {
  if (name === 'telephoneNumber') {
    return event.target.value.replace(/\s/g, '').slice(0, 10);
  }
  return event.target.value;
};

export const mapAttributeInputCallbackToComponent = (
  callback: AttributeInputCallback<string | number | boolean>,
): JSX.Element | undefined => {
  switch (callback.getType()) {
    case 'StringAttributeInputCallback': {
      if (callback.getName().startsWith('custom_section_')) {
        return <SectionTitle key={callback.payload._id} title={callback.getPrompt()} />;
      }

      if (['custom_gender', 'custom_professional'].includes(callback.getName())) {
        return handleChoiceSelect(callback);
      }

      let readOnly = false,
        hidden = false,
        disabled = false;
      let defaultValue = callback.getOutputValue('value') as string;
      if (callback.getName() === 'country') {
        readOnly = true;
        hidden = true;
        defaultValue = 'FRANCE';
        callback.setValue(defaultValue);
      }

      if (callback.getName() === 'city' || callback.getName() === 'postalAddress' || callback.getName() === 'custom_address2') {
        readOnly = true;
        disabled = true;
      }

      const getNode = (name: string): HTMLInputElement | null => {
        const node = document.getElementsByName(name);
        return node.length > 0 && node.item(0) instanceof HTMLInputElement ? (node.item(0) as HTMLInputElement) : null;
      };

      const getValueOfNode = (name: string) => getNode(name)?.value;

      const getAltValueOfNode = (name: string) => getNode(name)?.alt;

      if (callback.getName() === 'postalAddress') {
        const failedPolicyMessage = mapFailedPoliciesToMessage(callback.getFailedPolicies())[0];
        const regex = extractPolicy(callback, 'MATCH_REGEXP') as string;

        const onSelectedSearchChange = (selectedSearch?: Search) => {
          if (selectedSearch) {
            AutocompletionService.setFields(selectedSearch, regex);
          }
        };

        const buildAutocompletePayload = (value: string) => {
          const postalCode = getValueOfNode('postalCode') || '';
          const city = getValueOfNode('city') || '';
          const inseeCode = getAltValueOfNode('postalCode') || '';
          return {
            input: value,
            inseeCode,
            postalCode,
            city,
          } as AddressSearchInput;
        };

        return (
          <AutocompleteInput
            key={callback.payload._id}
            type="text"
            placeholder={callback.getPrompt()}
            name={callback.getName()}
            onChange={(event) => {
              callback.setValue(event.target.value);
              event.target.setCustomValidity('');
            }}
            fetchFunc={AddressSearchApi.fetchAddressesSearchWithDebounce}
            onSelectedSearchChange={onSelectedSearchChange}
            buildAutocompletePayload={buildAutocompletePayload}
            defaultValue={callback.getOutputValue('value') as string}
            failedPolicyMessage={failedPolicyMessage}
            required={callback.isRequired()}
            readOnly={readOnly}
            disabled={disabled}
            pattern={regex}
            onInvalid={(event: React.FormEvent<HTMLInputElement>) =>
              (event.target as HTMLInputElement).setCustomValidity(
                mapFailedPolicyToMessage({ policyRequirement: 'VALID_POSTAL_ADDRESS_FORMAT' }),
              )
            }
          />
        );
      }

      const failedPolicyMessage = mapFailedPoliciesToMessage(callback.getFailedPolicies())[0];
      const regex = extractPolicy(callback, 'MATCH_REGEXP') as string;
      const minLength = extractPolicy(callback, 'MIN_LENGTH') as number;
      const maxLength = extractPolicy(callback, 'MAX_LENGTH') as number;

      if (callback.getName() === 'postalCode') {
        const getSearchErrorMessage = (searchResults: Search[], error?: SearchError) => {
          if (error?.errorCode === 'INVALID_ZIP_CODE' || searchResults.length === 0) {
            return 'Code postal inconnu ou non supporté';
          }
          return 'Une erreur est survenue lors de la recherche de code postal';
        };

        const onSelectedSearchChange = (selectedSearch?: Search) => {
          const updatePostalCodeAlt = (value: string) => {
            const postalCodeElement = getNode('postalCode');
            if (postalCodeElement instanceof HTMLInputElement) {
              postalCodeElement.alt = value;
            }
          };

          if (!selectedSearch) {
            updatePostalCodeAlt('');
            AutocompletionService.unsetRelatedZipCodeFields();
            return;
          }

          AutocompletionService.setFields(selectedSearch, regex);

          if (AddressSearchApi.isInstanceOfZipCodeSearch(selectedSearch)) {
            updatePostalCodeAlt(selectedSearch.insee);
          }
        };

        const onEmptySearchResultOrError = (searchResults: Search[], searchErrorResult?: SearchError) => {
          const inputElement = document.getElementsByName(callback.getName() || '')[0] as HTMLInputElement;
          if (inputElement) {
            inputElement.setCustomValidity(getSearchErrorMessage ? getSearchErrorMessage(searchResults, searchErrorResult) : '');
            inputElement.reportValidity();
          }
          AutocompletionService.unsetRelatedZipCodeFields();
        };
        return (
          <AutocompleteInput
            key={callback.payload._id}
            type={mapCallbackToType(callback)}
            placeholder={callback.getPrompt()}
            name={callback.getName()}
            onChange={(event) => {
              callback.setValue(formatValue(event, callback.getName()));
              event.target.setCustomValidity('');
            }}
            onSelectedSearchChange={onSelectedSearchChange}
            onValidationChange={(valid: boolean | undefined) => {
              if (valid === false) {
                AutocompletionService.unsetRelatedZipCodeFields();
              }
            }}
            onEmptySearchResultOrError={onEmptySearchResultOrError}
            fetchFunc={AddressSearchApi.fetchZipCodeSearchWithDebounce}
            minAutoCompleteLength={5}
            runAutoCompleteOnDefaultValue={true}
            defaultValue={defaultValue}
            failedPolicyMessage={failedPolicyMessage}
            required={callback.isRequired()}
            readOnly={readOnly}
            disabled={disabled}
            pattern={regex}
            minLength={minLength}
            maxLength={maxLength}
            hidden={hidden}
          />
        );
      }

      return (
        <LabeledInput
          callback={callback}
          key={callback.payload._id + (addressGroupedElements.includes(callback.getName()) ? '_group' : '')}
          type={mapCallbackToType(callback)}
          placeholder={callback.getPrompt()}
          name={callback.getName()}
          onChange={(event) => {
            callback.setValue(formatValue(event, callback.getName()));
            event.target.setCustomValidity('');
          }}
          defaultValue={defaultValue}
          failedPolicyMessage={failedPolicyMessage}
          required={callback.isRequired()}
          readOnly={readOnly}
          disabled={disabled}
          pattern={regex}
          onInvalid={(event: React.FormEvent<HTMLInputElement>) =>
            (event.target as HTMLInputElement).setCustomValidity(mapFailedPolicyToMessage({ policyRequirement: 'DEFAULT' }))
          }
          minLength={minLength}
          maxLength={maxLength}
          hidden={hidden}
        />
      );
    }

    case 'BooleanAttributeInputCallback':
      return (
        <SlideToggle
          key={callback.payload._id}
          label={callback.getPrompt()}
          onChange={(value) => callback.setValue(value)}
          active={callback.getInputValue() as boolean}
        />
      );
  }
};
